import { ComponentStyleConfig } from '@chakra-ui/react';
import { getColorVar, mode, SystemStyleObject } from '@chakra-ui/theme-tools';

import { textStyles } from '../../common';
import { createVariables } from '../../../util';

const variables = createVariables<'bg' | 'glow' | 'icon' | 'stroke'>();

export const RadioStyles: ComponentStyleConfig = {
  parts: ['container', 'control', 'label'],
  baseStyle: (props) => {
    const containerDisabled: SystemStyleObject = {
      cursor: 'not-allowed',
    };

    const controlDisabled: SystemStyleObject = {
      '&&&': {
        [variables.bg]: getColorVar(props.theme, 'button.gray'),
        [variables.glow]: 'transparent',
        [variables.icon]: getColorVar(props.theme, 'state.disabled'),
        [variables.stroke]: getColorVar(props.theme, 'button.gray.outline'),
        opacity: 0.5,
      },
    };
    return {
      container: {
        [variables.bg]: getColorVar(props.theme, 'button.gray'),
        [variables.glow]: `var(${variables.icon})`,
        [variables.icon]: 'transparent',
        [variables.stroke]: getColorVar(props.theme, 'button.gray.outline'),
        _hover: {
          [variables.bg]: getColorVar(props.theme, 'button.gray.hover'),
        },
        _active: {
          [variables.bg]: getColorVar(props.theme, 'button.gray.active'),
        },
        _checked: {
          [variables.icon]: getColorVar(props.theme, 'state.selected'),
        },
        _invalid: {
          [variables.stroke]: getColorVar(props.theme, 'button.red.outline'),
          _checked: {
            [variables.icon]: getColorVar(props.theme, 'button.red'),
          },
        },
        _disabled: containerDisabled,
        '&:has([data-readonly])': containerDisabled,
      },
      control: {
        boxShadow: 'none',
        position: 'relative',
        border: '1px solid',
        outline: '2px solid transparent',
        outlineOffset: '1px',
        '&&&': {
          background: `var(${variables.bg})`,
          borderColor: `var(${variables.stroke})`,
          _checked: {
            backgroundImage: `radial-gradient(circle at center right, color-mix(in srgb, var(${variables.glow}) 40%, transparent), transparent 45%)`,
          },
        },
        _focusVisible: {
          outlineColor: 'state.selected',
        },
        '&&': {
          _before: {
            position: 'absolute',
            width: 'auto',
            height: 'auto',
            background: `var(${variables.icon})`,
            boxShadow: 'button',
          },
          _disabled: controlDisabled,
          _readOnly: controlDisabled,
        },
      },
      label: {
        _disabled: {
          opacity: 1,
          color: 'text.disabled',
        },
      },
    };
  },
  sizes: {
    sm: {
      control: {
        width: 3.5, // 14px
        height: 3.5, // 14px
        _before: {
          inset: '3px',
        },
      },
      label: {
        ...textStyles['body-sm'],
      },
    },
    md: {
      control: {
        height: 4.5, // 18px
        width: 4.5, // 18px
        _before: {
          inset: '4px',
        },
      },
      label: {
        ...textStyles['body-md'],
      },
    },
    lg: {
      control: {
        width: 5.5, // 22px
        height: 5.5, // 22px
        _before: {
          inset: '5px',
        },
      },
      label: {
        ...textStyles['body-md'],
      },
    },
  },
  variants: {
    box: (props) => ({
      // hide the radio circle
      control: {
        display: 'none',
        visibility: 'none',
        overflow: 'hidden',
        h: 0,
        w: 0,
      },
      container: {
        px: 3,
        py: 2,
        borderRadius: 'base',
        bg: mode('gray.50', 'gray.800')(props),
        borderColor: mode('gray.300', 'gray.700')(props),
        opacity: 0.6,
        _disabled: {
          opacity: 0.4,
        },
        _hover: {
          bgColor: mode('gray.50', 'gray.800')(props),
          borderColor: mode('gray.300', 'gray.700')(props),
          opacity: 1,
        },
        _checked: {
          opacity: 1,
        },
      },
      label: {
        ml: 0,
        textTransform: 'none',
      },
    }),
    subLabel: {
      // these styles align the control inline with the first line of text
      control: {
        top: '2px',
      },
      container: {
        alignItems: 'baseline',
      },
    },
  },
  defaultProps: {
    errorBorderColor: 'red.500',
  },
};
